import React, { useEffect, useState } from "react";
import { TextField, MenuItem, CircularProgress } from "@mui/material";
import BaseModal from "../BaseModal/index.tsx";
import adminService from "../../../services/adminService.ts";
import planosService from "../../../services/planosService.ts";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";
import Button from "../../../ui/Button.tsx";

interface PlanoModelo {
  id: string;
  nome: string;
}

interface EmpresaAdmin {
  id: string;
  nome: string;
  planoModeloId: string | null;
  ciclo: string | null;
  dataExpiracao: string | null;
}

interface AlterarLiberacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (empresa: EmpresaAdmin) => void;
  empresa: EmpresaAdmin | null;
}

// Mesma receita de cores dos Drawers — sem ThemeProvider do MUI, estados de
// label/borda caem no tema claro padrão do MUI, sem contraste no dark mode.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
  },
  "& .MuiSvgIcon-root": { color: "var(--ink-faint)" },
  "& input::-webkit-calendar-picker-indicator": { filter: "invert(0.6)" },
};

// O menu de um Select do MUI renderiza num Portal fora da árvore do
// componente — CSS escopado não alcança, precisa estilizar aqui também
// (mesmo problema já visto com o autofill do Chrome nos outros Drawers).
//
// zIndex explícito acima de 1302 (ModalPriorityEnum.LOW, usado pelo
// BaseModal que envolve esse Select) — sem isso o Menu do Select nasce com
// o z-index padrão do MUI (1300, já que o app não tem ThemeProvider), fica
// ATRÁS do overlay do modal, e as opções ficam invisíveis/inclicáveis.
const selectMenuProps = {
  sx: { zIndex: 1400 },
  PaperProps: {
    sx: {
      bgcolor: "var(--surface)",
      color: "var(--ink)",
      border: "1px solid var(--border)",
      "& .MuiMenuItem-root:hover": { bgcolor: "var(--surface-2)" },
      "& .MuiMenuItem-root.Mui-selected": { bgcolor: "var(--surface-2)" },
      "& .MuiMenuItem-root.Mui-selected:hover": { bgcolor: "var(--surface-2)" },
    },
  },
};

const AlterarLiberacaoModal: React.FC<AlterarLiberacaoModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  empresa,
}) => {
  const { showFeedback } = useFeedback();
  const [planos, setPlanos] = useState<PlanoModelo[]>([]);
  const [dataExpiracao, setDataExpiracao] = useState("");
  const [planoModeloId, setPlanoModeloId] = useState("");
  const [ciclo, setCiclo] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (isOpen) {
      planosService.listarPlanos().then((result) => {
        setPlanos(Array.isArray(result?.data) ? result.data : []);
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (empresa) {
      setDataExpiracao(empresa.dataExpiracao ? empresa.dataExpiracao.slice(0, 10) : "");
      setPlanoModeloId(empresa.planoModeloId || "");
      setCiclo(empresa.ciclo || "MONTHLY");
    }
  }, [empresa]);

  if (!empresa) return null;

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      let atualizado: EmpresaAdmin | undefined;

      // Só chama o endpoint de plano se um plano real foi escolhido — o
      // backend não aceita "limpar" o plano (planoModeloId é obrigatório
      // em AlterarPlanoAdminRequest), então deixar em branco só significa
      // "não mexer no plano atual" e altera apenas a data.
      if (planoModeloId) {
        atualizado = await adminService.alterarPlano(empresa.id, planoModeloId, ciclo || "MONTHLY");
      }
      if (dataExpiracao) {
        atualizado = await adminService.alterarLiberacao(empresa.id, dataExpiracao);
      }

      if (atualizado) onUpdate(atualizado);
      showFeedback("Alteração salva com sucesso!", "success");
      onClose();
    } catch (error: any) {
      showFeedback(error?.message || "Erro ao salvar alteração", "error");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Alterar plano / liberação">
      <div style={{ width: 420, maxWidth: "100%" }}>
        <p style={{ color: "var(--ink-faint)", fontSize: 13.2, marginTop: -6, marginBottom: 20 }}>
          {empresa.nome}
        </p>

        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <TextField
            select label="Plano" size="small" sx={{ ...fieldSx, flex: 1, minWidth: 160 }}
            value={planoModeloId} onChange={(e) => setPlanoModeloId(e.target.value)}
            SelectProps={{ MenuProps: selectMenuProps }}
          >
            <MenuItem value="">Sem plano</MenuItem>
            {planos.map((plano) => (
              <MenuItem key={plano.id} value={plano.id}>{plano.nome}</MenuItem>
            ))}
          </TextField>

          <TextField
            select label="Ciclo" size="small" sx={{ ...fieldSx, flex: 1, minWidth: 130 }}
            value={ciclo} onChange={(e) => setCiclo(e.target.value)}
            SelectProps={{ MenuProps: selectMenuProps }}
          >
            <MenuItem value="MONTHLY">Mensal</MenuItem>
            <MenuItem value="YEARLY">Anual</MenuItem>
          </TextField>
        </div>

        <div style={{ marginTop: 16 }}>
          <TextField
            type="date" label="Nova data de expiração" size="small" fullWidth sx={fieldSx}
            value={dataExpiracao} onChange={(e) => setDataExpiracao(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24, paddingTop: 18, borderTop: "1px solid var(--border)" }}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" onClick={handleSalvar} disabled={salvando || (!planoModeloId && !dataExpiracao)}>
            {salvando ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Salvar alteração"}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

export default AlterarLiberacaoModal;
